import React, { useMemo, useState } from "react";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { useAuth } from "../contexts/AuthContext";
import { AlertTriangle, ArrowRight } from "lucide-react";

/**
 * Iter52 — Conflicts Dialog shared by Manual / Bulk / Range save flows.
 *
 * The parent should call POST /api/attendance/conflicts/check first, then
 * mount this dialog with the returned `conflicts` + `cleanCount`. On Proceed
 * the dialog calls `onProceed({ resolutions, cleanCount, effectiveCount })`
 * — the parent then submits the actual save endpoint with `resolutions`.
 *
 * Actions per conflict row: skip (default) | overwrite | delete_and_recreate
 * | edit_existing (routes to the row's Edit dialog) | go_to_record
 * (closes create flow and navigates to the row in the list).
 */

const ACTIONS = [
    { value: "skip", label: "Skip", tone: "text-slate-600" },
    { value: "overwrite", label: "Overwrite", tone: "text-amber-700" },
    { value: "delete_and_recreate", label: "Delete + recreate", tone: "text-rose-700" },
    { value: "edit_existing", label: "Edit existing", tone: "text-sky-700" },
    { value: "go_to_record", label: "Go to record", tone: "text-slate-700" },
];

const SOURCE_TONE = {
    biometric: "bg-emerald-50 text-emerald-700 border-emerald-200",
    biometric_real: "bg-emerald-50 text-emerald-700 border-emerald-200",
    biometric_mock: "bg-emerald-50 text-emerald-700 border-emerald-200",
    manual: "bg-slate-50 text-slate-700 border-slate-200",
    manual_override: "bg-amber-50 text-amber-700 border-amber-200",
    mobile: "bg-indigo-50 text-indigo-700 border-indigo-200",
};

const sourceLabel = (s) => {
    if (!s) return "MANUAL";
    if (s === "manual_override") return "MANUALLY ADJUSTED";
    if (s.startsWith("biometric")) return "BIOMETRIC";
    return s.toUpperCase();
};

export default function AttendanceConflictsDialog({
    open,
    onOpenChange,
    conflicts = [],
    cleanCount = 0,
    onProceed,           // (resolutions, plan) => void
    onEditExisting,      // (conflict) => void  — routes to the row's Edit dialog
    onGoToRecord,        // (conflict) => void  — closes save flow, navigates to row
}) {
    const { user } = useAuth();
    const isAdminOrHr = user && ["kaafi_system_admin", "kaafi_hr_manager", "asl_hr_officer"].includes(user.role);

    const initialActions = useMemo(() => {
        const m = {};
        for (const c of conflicts) {
            m[`${c.employee_id}::${c.work_date}`] = "skip";
        }
        return m;
    }, [conflicts]);

    const [actions, setActions] = useState(initialActions);
    React.useEffect(() => { setActions(initialActions); }, [initialActions]);

    const setActionFor = (key, value) => setActions((a) => ({ ...a, [key]: value }));

    const applyAll = (value) => {
        const next = {};
        for (const c of conflicts) {
            const key = `${c.employee_id}::${c.work_date}`;
            // Skip approved rows for overwrite/delete when the user isn't Admin/HR.
            if (c.existing_row.approved && !isAdminOrHr &&
                ["overwrite", "delete_and_recreate"].includes(value)) {
                next[key] = "skip";
            } else {
                next[key] = value;
            }
        }
        setActions(next);
    };

    // Live count of rows the save would end up writing (create + overwrite + delete+recreate).
    const effectiveCount = useMemo(() => {
        let n = cleanCount;
        for (const c of conflicts) {
            const a = actions[`${c.employee_id}::${c.work_date}`] || "skip";
            if (a === "overwrite" || a === "delete_and_recreate") n += 1;
        }
        return n;
    }, [actions, conflicts, cleanCount]);

    const proceed = () => {
        // Route out any edit_existing / go_to_record picks first — those are
        // single-record actions, not batch save actions.
        const jumpEdit = conflicts.find((c) => actions[`${c.employee_id}::${c.work_date}`] === "edit_existing");
        if (jumpEdit) { onEditExisting?.(jumpEdit); return; }
        const jumpGo = conflicts.find((c) => actions[`${c.employee_id}::${c.work_date}`] === "go_to_record");
        if (jumpGo) { onGoToRecord?.(jumpGo); return; }

        const resolutions = conflicts.map((c) => ({
            employee_id: c.employee_id,
            work_date: c.work_date,
            action: actions[`${c.employee_id}::${c.work_date}`] || "skip",
        })).filter((r) => ["skip", "overwrite", "delete_and_recreate"].includes(r.action));

        onProceed?.(resolutions, { cleanCount, effectiveCount, conflictCount: conflicts.length });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl" data-testid="attendance-conflicts-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 asl-crimson-text" />
                        Some of these already have attendance today
                    </DialogTitle>
                    <p className="text-xs text-[color:var(--asl-muted)] mt-1">
                        Choose what to do for each. Skipping keeps the existing record.
                        Overwriting replaces its status / times. Delete + recreate removes
                        the old row and creates a fresh one.
                    </p>
                </DialogHeader>

                {/* Source badge legend */}
                <div className="flex flex-wrap gap-2 pt-1 pb-2 text-[10px]">
                    <SourceBadge source="biometric" showText />
                    <SourceBadge source="manual" showText />
                    <SourceBadge source="manual_override" showText />
                </div>

                {/* Apply to all — convenience row */}
                <div className="flex flex-wrap items-center gap-2 border-t border-b py-2 text-xs"
                     style={{ borderColor: "var(--asl-border, #E5E7EB)" }}>
                    <span className="text-[color:var(--asl-muted)] mr-1">Apply to all:</span>
                    <Button size="sm" variant="outline" onClick={() => applyAll("skip")}
                            data-testid="conflicts-apply-all-skip">Skip all</Button>
                    <Button size="sm" variant="outline" onClick={() => applyAll("overwrite")}
                            data-testid="conflicts-apply-all-overwrite">Overwrite all</Button>
                    <Button size="sm" variant="outline" onClick={() => applyAll("delete_and_recreate")}
                            data-testid="conflicts-apply-all-delete">Delete all</Button>
                </div>

                {/* Conflicts list */}
                <div className="max-h-[45vh] overflow-y-auto space-y-1.5 py-2" data-testid="conflicts-list">
                    {conflicts.map((c) => {
                        const key = `${c.employee_id}::${c.work_date}`;
                        const cur = actions[key] || "skip";
                        const approved = !!c.existing_row.approved;
                        const cannotOverwrite = approved && !isAdminOrHr;
                        return (
                            <div
                                key={key}
                                className="border rounded-md p-2.5 text-xs"
                                style={{ borderColor: "var(--asl-border, #E5E7EB)" }}
                                data-testid={`conflict-row-${c.employee_id}-${c.work_date}`}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium asl-navy-text truncate">
                                            {c.employee_name || "—"}{" "}
                                            <span className="text-[color:var(--asl-muted)] mono ml-1">
                                                {c.employee_code}
                                            </span>
                                        </div>
                                        <div className="text-[color:var(--asl-muted)] mono mt-0.5">
                                            {c.work_date}
                                            {approved && (
                                                <span className="ml-2 text-[10px] uppercase font-bold bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5">
                                                    approved
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-1 flex flex-wrap items-center gap-2">
                                            <SourceBadge source={c.existing_row.source} />
                                            <span className="text-[10px] uppercase font-bold bg-slate-100 text-slate-700 rounded-full px-2 py-0.5">
                                                {c.existing_row.status}
                                            </span>
                                            {c.existing_row.check_in_time && (
                                                <span className="text-[10px] text-[color:var(--asl-muted)] mono">
                                                    IN {c.existing_row.check_in_time}
                                                </span>
                                            )}
                                            {c.existing_row.check_out_time && (
                                                <span className="text-[10px] text-[color:var(--asl-muted)] mono">
                                                    OUT {c.existing_row.check_out_time}
                                                </span>
                                            )}
                                            {c.existing_row.worked_minutes > 0 && (
                                                <span className="text-[10px] text-[color:var(--asl-muted)] mono">
                                                    {Math.floor(c.existing_row.worked_minutes / 60)}h {c.existing_row.worked_minutes % 60}m
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-2 flex flex-wrap gap-1">
                                    {ACTIONS.map((a) => {
                                        const disabled = cannotOverwrite && ["overwrite", "delete_and_recreate"].includes(a.value);
                                        const active = cur === a.value;
                                        return (
                                            <button
                                                key={a.value}
                                                type="button"
                                                disabled={disabled}
                                                onClick={() => setActionFor(key, a.value)}
                                                title={disabled ? "This row is approved. Only Admin can overwrite/delete." : ""}
                                                className={
                                                    "px-2 py-1 rounded-md border text-[11px] transition " +
                                                    (active
                                                        ? "asl-navy-bg text-white border-transparent"
                                                        : disabled
                                                            ? "text-slate-400 border-slate-200 bg-slate-50 cursor-not-allowed"
                                                            : "text-slate-700 border-slate-200 hover:bg-slate-50")
                                                }
                                                data-testid={`conflict-action-${c.employee_id}-${c.work_date}-${a.value}`}
                                            >
                                                {a.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <DialogFooter className="flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <div className="flex-1 text-xs text-[color:var(--asl-muted)]" data-testid="conflicts-effective-count">
                        {cleanCount} clean · {conflicts.length} conflict{conflicts.length === 1 ? "" : "s"} ·{" "}
                        <b className="asl-navy-text">{effectiveCount}</b> row{effectiveCount === 1 ? "" : "s"} will be written
                    </div>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="conflicts-cancel">
                        Cancel
                    </Button>
                    <Button
                        className="asl-navy-bg text-white"
                        onClick={proceed}
                        disabled={effectiveCount === 0 &&
                                  !conflicts.some((c) => ["edit_existing", "go_to_record"].includes(actions[`${c.employee_id}::${c.work_date}`]))}
                        data-testid="conflicts-proceed"
                    >
                        Proceed with {effectiveCount} row{effectiveCount === 1 ? "" : "s"}
                        <ArrowRight className="h-3.5 w-3.5 ml-1" />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function SourceBadge({ source, showText }) {
    const cls = SOURCE_TONE[source] || SOURCE_TONE.manual;
    return (
        <span
            className={"text-[10px] uppercase font-bold rounded-md px-2 py-0.5 border " + cls}
            data-testid={`source-badge-${source}`}
        >
            {showText ? sourceLabel(source) : sourceLabel(source)}
        </span>
    );
}
